import React, {Dispatch, useCallback, useEffect, useRef, useState} from "react";
import {useChatTrackedState, useChatDispatch} from "./store";
import {NewChannelAction, NewChannelState} from "./CGCOptions";
import {FlexScroll, Grid, TagInputContainer, TextInformationContainer} from "./styles";
import ChannelIcon from "./ChannelIcon";
import ChannelTitle from "./ChannelTitle";
import ChannelSubtitle from "./ChannelSubtitle";
import {TagInput} from "@dspworkplace/ui";
import {LoadingView} from "../components/Loading";

type Props = {
    newChannelModalState: NewChannelState;
    newChannelModalDispatch: Dispatch<NewChannelAction>;
    closeModal: () => void;
};

function CGCSearchGroupChannels(props: Props) {
    const {newChannelModalState, newChannelModalDispatch, closeModal} = props;
    const state = useChatTrackedState();
    const {groupConversations, conversationClient} = state;
    const dispatch = useChatDispatch();
    const [loading, setLoading] = useState(true);
    const {searchGroupChannelsNames, searchGroupChannelsMatched} = newChannelModalState;
    const mounted = useRef(false);
    const groupChannelsCount = useRef(groupConversations.length);

    useEffect(() => {
        const abortController = new AbortController();
        if (mounted.current) return;
        if (conversationClient && groupConversations) {
            mounted.current = true;
            groupChannelsCount.current = groupConversations.length;
            newChannelModalDispatch({type: "SET_GROUP_CHANNELS", searchGroupChannels: groupConversations});
            setLoading(false);
        }
        return () => {
            abortController.abort();
        };
    }, [conversationClient, groupConversations, newChannelModalDispatch]);

    useEffect(() => {
        const abortController = new AbortController();
        if (conversationClient && groupConversations.length > groupChannelsCount.current) {
            newChannelModalDispatch({type: "ADD_GROUP_CHANNEL", searchGroupChannel: groupConversations[0]});
        }
        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newChannelModalDispatch, groupConversations.length]);

    const openChannel = useCallback(
        (item) => () => {
            if (item && item.sid) {
                setLoading(true);
                dispatch({type: 'ADD_ACTIVE_CONVERSATION', conversation: item});
                setLoading(false);
                closeModal();
            }
        },
        [closeModal, dispatch]
    );

    const handleTagChange = useCallback(
        data => {
            newChannelModalDispatch({type: "SEARCH_GROUP_CHANNELS", data: data});
        },
        [newChannelModalDispatch]
    );

    if (loading) {
        return <LoadingView containerHeight={"400px"} containerWidth={"100%"} imgHeight={"150px"}/>;
    }

    return (
        <Grid templateRows={"auto auto"} rowGap={"16px"}>
            <TagInputContainer templateRows={"auto"} width={'658px'}>
                <TagInput
                    label="Search Group Channels:"
                    options={searchGroupChannelsNames}
                    size="big"
                    onChange={handleTagChange}
                    name={'groups'}
                />
            </TagInputContainer>
            <FlexScroll height="300px" width="100%">
                <Grid templateColumns={"auto auto auto"} columnGap={"8px"} rowGap={"8px"}>
                    {searchGroupChannelsMatched.map(item => {
                        // @ts-ignore
                        return (
                            <Grid
                                key={item.sid}
                                templateColumns={"40px auto"}
                                height={"56px"}
                                columnGap={"8px"}
                                padding={"4px"}
                                border={"1px dashed #7C98B6"}
                                width={"100%"}
                                borderRadius={"4px"}
                                onClick={openChannel(item)}
                            >
                                <ChannelIcon
                                    conversation={item}
                                    // @ts-ignore
                                    kind={item.attributes['kind']}/>
                                <TextInformationContainer>
                                    <ChannelTitle
                                        conversation={item}
                                        // @ts-ignore
                                        kind={item.attributes['kind']}/>
                                    <ChannelSubtitle
                                        conversation={item}
                                        // @ts-ignore
                                        kind={item.attributes['kind']}/>
                                </TextInformationContainer>
                            </Grid>
                        );
                    })}
                </Grid>
            </FlexScroll>
        </Grid>
    );
}

export default CGCSearchGroupChannels;
